import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Fund } from 'src/app/domain/fund-performance/dto/Fund';
import { Subject } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalButtons, SpinnerProps } from 'src/app/domain/shared/component/modal-container/modal-container.component';
import { RegExpService } from 'src/app/util/common/reg-exp.service';
import { Countries } from '../../../../../util/common/countries';
import { FundService } from '../../../service/fund.service';
import { Organization } from '../../../dto/Organization';
import { NotificationsService } from '../../../../../core/service/notifications.service';
import { ManagementEntity } from '../../../dto/ManagementEntity';
import { BaseFundDateType } from '../modal-fund-term/modal-fund-term.component';
import { OrganizationAccesses } from '../../service/organizations.service';

@Component({
  selector: 'app-modal-fund-update',
  templateUrl: './modal-fund-update.component.html',
  styleUrls: ['./modal-fund-update.component.scss'],
})

export class ModalFundUpdateComponent implements OnInit, OnChanges, OnDestroy {
  readonly regExpService = RegExpService;

  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() fund: Fund;
  @Input() funds: Fund[]; // mean time not in use
  @Input() modalType: 'create' | 'update' | 'copy' = 'create';
  @Input() orgAccess: OrganizationAccesses;
  @Input() selectedOrg: Organization;
  @Input() selectedManagementCompany: ManagementEntity;
  @Input() managementCompanies: ManagementEntity[];
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter();

  currentFund: FundAndAction;
  showFundDeleteModal = false;
  showFundTermsModal = false;
  selectedManagementCompanyUUID;
  countries: string[];

  selectedFundForm: FormGroup;

  modalButtons: ModalButtons = {
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  additionalClass = {
    name: '',
    fundId: '',
  };

  countedPeriodType: BaseFundDateType;

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  constructor(
    private formBuilder: FormBuilder,
    private fundService: FundService,
    private notificationService: NotificationsService,
  ) {
  }

  ngOnChanges(): void {
    this.createForm();
    this.currentFund = { fund: this.selectedFundForm.value };
    if (this.modalType === 'create' || this.modalType === 'copy') {
      this.selectedFundForm.patchValue({ expectedOpEx: 20.0 });
      this.selectedFundForm.patchValue({ investmentLimit: 20.0 });
      this.selectedFundForm.patchValue({ fundId: this.generateFundId() });
    }
    if (this.orgAccess && this.modalType === 'update') {
      this.selectedFundForm.patchValue({ expectedOpEx: this.fund.expectedOpEx * 100 });
      this.modalButtons.lowerLeft = 'Delete';
      this.modalButtons.lowerLeftIcon = 'delete.svg';
      this.modalButtons.lowerLeftClass = 'light-red';
    }
  }

  ngOnInit(): void {
    this.countries = Countries;
    this.selectedManagementCompany = this.fund?.managementCompany;
    this.selectedManagementCompanyUUID = this.selectedManagementCompany?.uuid;
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  checkFundId(): void {
    const fundId = this.selectedFundForm.value.fundId;
    if (this.fund?.fundId === fundId) {
      return;
    }
    const len = this.selectedOrg.funds.filter((el) => el.fundId === fundId).length;
    if (len) {
      this.selectedFundForm.patchValue({ fundId: undefined });
      this.notificationService.error({
        title: 'Fund id already exists',
        message: 'Please enter another fund id:',
      });
    }
  }

  confirm(): void {
    if (this.selectedFundForm.invalid) {
      this.additionalClass.name = this.selectedFundForm.controls.name.invalid ? 'error' : '';
      this.additionalClass.fundId = this.selectedFundForm.controls.fundId.invalid ? 'error' : '';
      return;
    }
    if (this.modalType === 'update') {
      this.updateFund();
    } else if (this.modalType === 'create') {
      this.createFund();
    } else if (this.modalType === 'copy') {
      this.copyFund();
    }
  }

  async copyFund(): Promise<void> {
    await this.fundService.copyFund(this.selectedFundForm.value);
    this.notificationService.success({
      title: 'Fund duplicated',
      message: 'Successfully duplicated fund: ' + this.fund.name,
    });
    this.closeModal.emit(true);
  }

  async createFund(): Promise<void> {
    this.selectedFundForm.patchValue({ investmentLimit: this.selectedFundForm.value.investmentLimit / 100 });
    this.selectedFundForm.patchValue({ expectedOpEx: this.selectedFundForm.value.expectedOpEx / 100 });
    await this.fundService.create(this.selectedFundForm.value, this.selectedOrg.uuid, this.selectedManagementCompanyUUID);
    this.notificationService.success({
      title: 'Fund created',
      message: 'Successfully created fund: ' + this.selectedFundForm.value.name,
    });
    this.closeModal.emit(true);
  }

  async updateFund(): Promise<void> {
    const prevEntityUUID = this.fund.managementCompany ? this.fund.managementCompany.uuid : null;
    // eslint-disable-next-line no-nested-ternary
    this.selectedManagementCompanyUUID = this.selectedManagementCompanyUUID === prevEntityUUID ?
      null : this.selectedManagementCompanyUUID ? this.selectedManagementCompanyUUID : null;
    try {
      await this.fundService.update(this.selectedFundForm.value, this.selectedManagementCompanyUUID, prevEntityUUID);
      this.notificationService.success({
        title: 'Fund updated',
        message: 'Successfully updated fund: ' + this.selectedFundForm.value.name,
      });
      this.closeModal.emit(true);
    } catch (err) {
      this.closeModal.emit(true);
    }
  }

  async closeDeleteModal(isConfirmed: boolean): Promise<void> {
    if (isConfirmed) {
      await this.deleteFund();
    }
    this.showFundDeleteModal = false;
  }

  async deleteFund(): Promise<void> {
    try {
      await this.fundService.delete(this.fund.uuid);
      this.closeModal.emit(true);
    } catch (err) {
      this.closeModal.emit(true);
    }
  }

  showModalFundTerm(type: BaseFundDateType): void {
    this.countedPeriodType = type;
    this.showFundTermsModal = true;
  }

  setCountedPeriod(countedPeriod: string): void {
    this.showFundTermsModal = false;
    switch (this.countedPeriodType) {
      case 'Final closing':
        this.selectedFundForm.patchValue({ finalClosing: countedPeriod });
        break;
      case 'Investment period':
        this.selectedFundForm.patchValue({ investmentPeriod: countedPeriod });
        break;
      case 'Fund term':
        this.selectedFundForm.patchValue({ fundTerm: countedPeriod });
        break;
      default:
        break;
    }
  }


  private generateFundId(): string {
    const lastFundId = this.funds
      .map((f) => Number(f.fundId))
      .filter((id) => !isNaN(id))
      .sort((a, b) => b - a)[0] ?? 0;
    return (lastFundId + 1).toString();
  }

  private createForm(): void {
    this.selectedFundForm = this.formBuilder.group({
      id: new FormControl(this.fund?.id, { updateOn: 'change' }),
      uuid: new FormControl(this.fund?.uuid, { updateOn: 'change' }),
      name: new FormControl(this.modalType !== 'copy' ? this.fund?.name : null,
        { validators: [Validators.required, Validators.pattern('\\s*\\S.*')], updateOn: 'change' }),
      fundId: new FormControl(this.modalType !== 'copy' ? this.fund?.fundId : null,
        {
          validators: [Validators.required, Validators.pattern(RegExpService.notNegativeDecimalNumbersOrEmpty)],
          updateOn: 'change',
        }),
      registration: new FormControl(this.modalType !== 'copy' ? this.fund?.registration : null, { updateOn: 'change' }),
      registrationDate: new FormControl(this.modalType !== 'copy' ? this.fund?.registrationDate : null,
        { updateOn: 'change' }),
      initialClosing: new FormControl(this.modalType !== 'copy' ? this.fund?.initialClosing : null, { updateOn: 'change' }),
      finalClosing: new FormControl(this.modalType !== 'copy' ? this.fund?.finalClosing : null, { updateOn: 'change' }),
      investmentPeriod: new FormControl(this.modalType !== 'copy' ? this.fund?.investmentPeriod : null,
        { updateOn: 'change' }),
      fundTerm: new FormControl(this.modalType !== 'copy' ? this.fund?.fundTerm : null, { updateOn: 'change' }),
      expectedOpEx: new FormControl(this.fund?.expectedOpEx, { updateOn: 'change' }),
      investmentLimit: new FormControl(this.fund?.investmentLimit * 100, { updateOn: 'change' }),
      registrationNumber: new FormControl(this.modalType !== 'copy' ? this.fund?.registrationNumber : null,
        { updateOn: 'change' }),
      spv: new FormControl(this.modalType !== 'copy' ? this.fund?.spv : null,
        { updateOn: 'change' }),
    });
  }
}

export interface FundAndAction {
  fund?: Fund;
  modalType?: FundModalType;
}

export enum FundModalType {
  CREATE = 'create',
  UPDATE = 'update',
  COPY = 'copy',
}
